import SceneProducts from '@elem/compCommon/SceneProducts';
import { useScrollAnimation, cn } from 'elem/compPage/battery-backup-for-home/scroll-animation-v2';
import EcoImg from 'eco-design/base/eco-img';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import { getTabsConfig, TAB_KEY } from './config';
import { useLang } from '@/i18n';

export default function Preparing() {
    const { t } = useLang('powerbackup.Preparing');
    const { t: s } = useLang('powerbackup');
    const [activeTab, setActiveTab] = useState<TAB_KEY>(TAB_KEY.HOURS);

    const tabs = useMemo(() => getTabsConfig(t), [t]);
    const currentTab = tabs.find((tab) => tab.key === activeTab) || tabs[0];

    // Tab滑动指示器相关
    const tabsContainerRef = useRef<HTMLDivElement>(null);
    const tabRefs = useRef<Map<TAB_KEY, HTMLButtonElement>>(new Map());
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

    // 计算indicator位置
    const updateIndicator = useCallback(() => {
        const container = tabsContainerRef.current;
        const activeTabEl = tabRefs.current.get(activeTab);
        if (container && activeTabEl) {
            const containerRect = container.getBoundingClientRect();
            const tabRect = activeTabEl.getBoundingClientRect();
            setIndicatorStyle({
                left: tabRect.left - containerRect.left,
                width: tabRect.width,
            });
        }
    }, [activeTab]);

    // activeTab变化时更新indicator位置
    useEffect(() => {
        updateIndicator();
    }, [updateIndicator]);

    // 窗口resize时重新计算
    useEffect(() => {
        window.addEventListener('resize', updateIndicator);
        return () => window.removeEventListener('resize', updateIndicator);
    }, [updateIndicator]);

    const { ref, isVisible } = useScrollAnimation({
        threshold: 0.1,
    });

    return (
        <div ref={ref} className={`${styles.Preparing} GtmModuleData-section`}>
            <div className="pb-layout">
                <h2 className={cn(isVisible, 'power-backup-title fade-up')}>{s('secD_title')}</h2>
            </div>

            {/* 移动端吸顶容器：tabs在这个wrapper内sticky */}
            <div className="preparing-sticky-wrapper">
                <div className=" preparing-tabs-container">
                    <div
                        ref={tabsContainerRef}
                        className={cn(isVisible, 'preparing-tabs fade-up')}
                        style={{ '--animate-delay': '0.15s' } as React.CSSProperties}
                    >
                        {/* 滑动指示器 */}
                        <div
                            className="tab-indicator"
                            style={{
                                transform: `translateX(${indicatorStyle.left}px)`,
                                width: `${indicatorStyle.width}px`,
                            }}
                        />
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                ref={(el) => {
                                    if (el) tabRefs.current.set(tab.key, el);
                                }}
                                className={`tab-item ${activeTab === tab.key ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.tabLabel}
                            </button>
                        ))}
                    </div>
                </div>

                <div className={cn(isVisible, 'preparing-banner fade-up')} style={{ '--animate-delay': '0.3s' } as React.CSSProperties}>
                    <EcoImg className="banner-image" pc={currentTab.image.pc} h5={currentTab.image.h5} />
                    <div className="banner-content pb-x-layout ">
                        <div className="banner-label">{currentTab.label}</div>
                        <h3 className="banner-title">{currentTab.title}</h3>
                        <p className="banner-desc">{currentTab.desc}</p>
                    </div>
                </div>

                <SceneProducts
                    key={currentTab.key}
                    id={`power-backup-products-${currentTab.key}`}
                    className="au-preparing-products"
                    cards={currentTab.products}
                    texts={{
                        buyNow: t('btn1'),
                        more: t('btn2'),
                        out: t('btn_sold_out'),
                        listName: currentTab.title,
                    }}
                />
            </div>
        </div>
    );
}
